import { Navigate } from "react-router-dom";
import type { ReactNode } from "react";
import { useCapabilities, type Capability } from "../auth/capabilities";
import { ForbiddenPage } from "../pages/ForbiddenPage";

// Route-level authorization guard. Renders its children only when the session holds
// the required capability; otherwise it bounces to the learner home. This backs the
// nav gating in AppLayout so a hand-typed /admin URL can't reach an admin view either
// (defence in depth — the API still enforces authorization). RequireAuth runs first
// on the parent route, so a session is guaranteed to exist here.
// A list of capabilities means ANY of them opens the route — for a page that serves two audiences whose
// capabilities overlap only partly (the muutoskartta: changeProgramManagement opens the LIVE map and
// changePublishedReportRead the published one, and the two sets share only the consultant). The page then
// decides what to render per capability; the guard only decides whether it may be opened at all. The
// pattern's other user was Sisällönhallinta, which carried the courses and the questionnaires behind
// `contentManagement || questionnaireManagement`; the owner's decision of 3.11.2026 split it into two
// pages with one capability each, so that route takes a single capability now.
//
// WHAT A REFUSAL LOOKS LIKE — `denied`, and why there are two answers
// -------------------------------------------------------------------
// The default is `"home"`: the reader is silently moved to the learner page they do have. That is the
// right answer for the /admin and /reports tree, where the refused URL names a surface of THIS
// organisation that this reader is simply not an administrator of — they are at home in the app, just not
// here, and a refusal screen would be a dead end with nothing to do on it.
//
// `"forbidden"` renders the refusal instead (./pages/ForbiddenPage), and muutosjohtaminen is what it
// exists for (ADR-0016 §4.2, FR-Q.33). Its consultant is an Incedo person provisioned into the named
// customer tenants one at a time, so the realistic way to arrive at `/change-programs` without the role
// is to open a programme URL against the WRONG tenant — a tenant where the module is in use and this
// reader's own grant is somewhere else. Bouncing them home says "this does not exist", which is the one
// thing that is not true and the one answer they cannot act on; and the module's own stories state the
// refusal as a state to be rendered ("Rajattu / estetty: … → 403"), not as a redirect.
//
// Neither answer is a security boundary, and the choice between them changes nothing about what the API
// does: every route here is also gated server-side, and a reader who gets past this guard with a stale
// token meets the real 403 on the first read.
export function RequireRole({
  capability,
  denied = "home",
  children,
}: {
  capability: Capability | readonly Capability[];
  /** What a refused reader sees: the learner home (default), or the refusal itself. */
  denied?: "home" | "forbidden";
  children: ReactNode;
}) {
  const { can } = useCapabilities();
  const required = Array.isArray(capability) ? capability : [capability as Capability];
  if (!required.some(can)) {
    return denied === "forbidden" ? <ForbiddenPage /> : <Navigate to="/" replace />;
  }
  return <>{children}</>;
}
